import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaPlatform,
} from '@wix/panorama-client-web';
import type { PanoramaClient } from '@wix/panorama-client-web';

const FULL_ARTIFACT_ID = 'com.wixpress.users.login-react-app';
const COMPONENT_ID = 'login-react-app';

type PanoramaTransaction = ReturnType<PanoramaClient['transaction']>;
/** Optional per-transaction custom data, forwarded to Panorama start/finish. */
type TransactionData = Parameters<PanoramaTransaction['finish']>[0];

/**
 * The statics URL looks like
 * `https://static.parastorage.com/services/login-react-app/1.2815.0/...`.
 * The version segment (`artifactVersion`) is REQUIRED for gradual-rollout
 * attribution — without it Panorama can't tie transactions to the rolling
 * version and the "…Success Rate Delta" rollout rule stays empty. Returns
 * undefined for local/dev URLs that don't match, which Panorama tolerates.
 */
function parseArtifactVersion(staticsUrl?: string): string | undefined {
  const match = staticsUrl?.match(/\/services\/[^/]+\/([^/]+)/);
  return match?.[1];
}

function createPanoramaClient(): PanoramaClient | undefined {
  // Client creation wires up plugins (sentry, environment, …); guard it so a
  // monitoring-setup failure can never break app init in the RootStore ctor.
  try {
    return panoramaClientFactory({
      baseParams: {
        fullArtifactId: FULL_ARTIFACT_ID,
        artifactVersion: parseArtifactVersion(window.__STATICS_URL__),
        platform: PanoramaPlatform.Standalone,
      },
    })
      .withGlobalConfig(createGlobalConfig())
      .client({ baseParams: { componentId: COMPONENT_ID } });
  } catch {
    return undefined;
  }
}

/**
 * Panorama replaces the deprecated FedOps logger for this app. Component
 * transactions map 1:1 to the old FedOps interactions (`startTransaction` ↔
 * `interactionStarted`, `finishTransaction` ↔ `interactionEnded`) and the
 * component-load transaction replaces `appLoaded`. They also power the
 * Deployments-NG "Safer gradual rollout" gate ("Panorama Component Transaction
 * Success Rate Delta"). A transaction is a success once `finish()` is called;
 * a started-but-unfinished transaction is counted as a failure.
 */
export class PanoramaService {
  private readonly client = createPanoramaClient();
  private readonly openTransactions = new Map<string, PanoramaTransaction>();

  /** Starts the component-load transaction (page-load success rate). */
  reportLoadStart(): void {
    this.client?.reportLoadStart();
  }

  /** Marks the component load as successful. */
  reportLoadFinish(): void {
    this.client?.reportLoadFinish();
  }

  startTransaction(name: string, data?: TransactionData): void {
    const transaction = this.client?.transaction(name);
    if (transaction) {
      this.openTransactions.set(name, transaction);
      transaction.start(data);
    }
  }

  finishTransaction(name: string, data?: TransactionData): void {
    this.openTransactions.get(name)?.finish(data);
    this.openTransactions.delete(name);
  }
}
